import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { ImageSource } from "@app/shared";
import { api } from "../lib/api";
import { Label } from "./ui/label";

/**
 * Local image-source state for a generate form. Initializes to the global
 * default (`image_source` setting, falling back to "pexels") on first load, but
 * lets the operator override it per-generate — once they pick a value manually,
 * the global default no longer overrides their choice.
 */
export function useImageSourceState(): [ImageSource, (s: ImageSource) => void] {
  const { data } = useQuery({
    queryKey: ["imageSourceDefault"],
    queryFn: ({ signal }) => api.imageSourceDefault(signal),
    staleTime: 30_000,
  });
  const [value, setValue] = useState<ImageSource>("pexels");
  const touched = useRef(false);

  useEffect(() => {
    if (!touched.current && data) setValue(data);
  }, [data]);

  const set = (s: ImageSource) => {
    touched.current = true;
    setValue(s);
  };
  return [value, set];
}

/** A labeled "Sumber gambar" dropdown: Pexels (stock) vs Galeri (AI picks). */
export function ImageSourceSelect({
  value,
  onChange,
  id = "image-source",
  disabled,
}: {
  value: ImageSource;
  onChange: (s: ImageSource) => void;
  id?: string;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>Sumber gambar</Label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value as ImageSource)}
        disabled={disabled}
        className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal focus-visible:ring-offset-1 disabled:opacity-50"
      >
        <option value="pexels">Pexels (stok foto)</option>
        <option value="library">Galeri (AI pilih)</option>
      </select>
    </div>
  );
}
